import React from 'react';
import ReactDOM from 'react-dom';
import CheckIcon from '@mui/icons-material/Check';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';
import {
  GREEN,
  GREEN_SOFT,
  LAVENDER,
  LAVENDER_SOFT,
  INDIGO,
  SLATE,
  LINE,
  BG,
  GOLD,
  colorByName,
  pastelFor,
} from './raffleTokens';

/**
 * R13/R14 — a ticket number is never a plain number with a dot: it is a stub
 * in the ticket's own colour, with perforation notches, the colour name small
 * and the number large. Neon colours glow so they read apart from standard.
 */
export function TicketStub({ number, color, size = 'md', notchBg = '#fff' }) {
  const c =
    typeof color === 'string'
      ? colorByName(color)
      : color || colorByName('Yellow');
  const neon = c.group === 'Neon';
  const d = {
    sm: { h: 34, fs: 15, pad: 10, notch: 6, label: 7 },
    md: { h: 52, fs: 24, pad: 14, notch: 9, label: 8 },
    lg: { h: 84, fs: 42, pad: 20, notch: 13, label: 10 },
  }[size];

  return (
    <div
      className="rf-stub"
      style={{
        height: d.h,
        paddingLeft: d.pad + d.notch,
        paddingRight: d.pad + d.notch,
        background: c.hex,
        boxShadow: neon
          ? `0 0 ${d.h * 0.28}px ${
              c.hex
            }88, inset 0 0 0 1px rgba(255,255,255,0.35)`
          : 'inset 0 0 0 1px rgba(0,0,0,0.12), 0 1px 3px rgba(10,14,42,0.18)',
      }}
    >
      <span
        className="rf-stub-notch"
        style={{
          width: d.notch * 2,
          height: d.notch * 2,
          background: notchBg,
          left: -d.notch,
        }}
      />
      <span
        className="rf-stub-notch"
        style={{
          width: d.notch * 2,
          height: d.notch * 2,
          background: notchBg,
          right: -d.notch,
        }}
      />
      <span
        className="rf-stub-dash"
        style={{
          left: d.pad + d.notch - 5,
          top: d.notch * 0.7,
          bottom: d.notch * 0.7,
          borderLeft: `2px dashed ${c.ink}`,
        }}
      />
      <span className="rf-stub-text">
        <span
          style={{ fontSize: d.label, color: c.ink }}
          className="rf-stub-label"
        >
          {c.name.toUpperCase()}
        </span>
        <span
          style={{ fontSize: d.fs, color: c.ink }}
          className="rf-stub-number"
        >
          {number}
        </span>
      </span>
    </div>
  );
}

/** Machine number tile — the machine-raffle counterpart of the stub. */
export function MachineTile({ value, struck, dim, label = 'MCH' }) {
  return (
    <div className="rf-mtile" style={{ opacity: dim ? 0.55 : 1 }}>
      <span className="rf-mtile-label">{label}</span>
      <span
        className="rf-mtile-value"
        style={{ textDecoration: struck ? 'line-through' : 'none' }}
      >
        {value}
      </span>
    </div>
  );
}

export function Avatar({ name = '', size = 44, ring, photo, src }) {
  // A photo that fails to load falls back to initials rather than showing
  // the browser's broken-image alt text.
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => setBroken(false), [src]);
  const initials = name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('');
  return (
    <div
      className="rf-avatar"
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        background: pastelFor(name),
        fontSize: size * 0.34,
        boxShadow: ring ? `0 0 0 3px #fff, 0 0 0 6px ${GREEN}` : 'none',
      }}
    >
      {src && !broken ? (
        <img
          src={src}
          alt=""
          className="rf-avatar-img"
          style={{ borderRadius: size / 2 }}
          onError={() => setBroken(true)}
        />
      ) : (
        initials
      )}
      {photo && (
        <span
          className="rf-avatar-badge"
          style={{ width: size * 0.34, height: size * 0.34 }}
        >
          <PhotoCameraIcon style={{ fontSize: size * 0.18, color: '#fff' }} />
        </span>
      )}
    </div>
  );
}

export function Chip({ label, value, done, active }) {
  return (
    <div
      className="rf-chip"
      style={{
        background: done ? GREEN_SOFT : active ? LAVENDER_SOFT : '#fff',
        border: `1px solid ${done ? '#b7e2cc' : active ? LAVENDER : LINE}`,
      }}
    >
      {done && <CheckIcon style={{ fontSize: 14, color: GREEN }} />}
      <span
        className="rf-chip-label"
        style={{ color: done ? GREEN : active ? INDIGO : SLATE }}
      >
        {label}
      </span>
      {value !== undefined && value !== null && (
        <span className="rf-chip-value">{value}</span>
      )}
    </div>
  );
}

export const SectionLabel = ({ children, style }) => (
  <div className="rf-section-label" style={style}>
    {children}
  </div>
);

export const GroupBar = ({ Icon, children }) => (
  <div className="rf-groupbar">
    <Icon style={{ fontSize: 17, color: GOLD }} />
    <span>{children}</span>
  </div>
);

export function Toggle({ on, onClick, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={!!on}
      disabled={disabled}
      onClick={onClick}
      className="rf-toggle"
      style={{
        background: on ? GREEN : '#cdd3e0',
        opacity: disabled ? 0.5 : 1,
      }}
    >
      <span
        className="rf-toggle-knob"
        style={{ marginLeft: on ? 'auto' : 0 }}
      />
    </button>
  );
}

/**
 * Overlays render on <body>: inside the app shell they sit in the page's
 * stacking context and the app header covers their top edge. The .rf-root
 * wrapper keeps the module's styles applying out there.
 */
export function Overlay({ children }) {
  return ReactDOM.createPortal(
    <div className="rf-root rf-overlay-root">{children}</div>,
    document.body
  );
}

/** Bottom sheet on phones, centred dialog on wider screens. */
export function Sheet({ children, onClose, wide }) {
  return (
    <Overlay>
      <div
        className="rf-sheet-backdrop"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && onClose) onClose();
        }}
      >
        <div
          className={`rf-sheet ${wide ? 'rf-sheet-wide' : ''}`}
          role="dialog"
          aria-modal="true"
        >
          {children}
        </div>
      </div>
    </Overlay>
  );
}

export function SheetTitle({ Icon, iconColor = INDIGO, children, onClose }) {
  return (
    <div className="rf-sheet-title">
      <div className="rf-row rf-gap-8">
        {Icon && <Icon style={{ fontSize: 20, color: iconColor }} />}
        <span className="rf-h3">{children}</span>
      </div>
      {onClose && (
        <button
          type="button"
          className="rf-icon-btn"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon style={{ color: SLATE }} />
        </button>
      )}
    </div>
  );
}

/** – [ n ] + with a direct numeric input (R16, R76). */
export function Stepper({ value, onChange, min, max }) {
  const clamp = (v) => Math.max(min, Math.min(max, v));

  // What the user has typed, held here until they are finished with the
  // field. `null` means they aren't editing and the input simply shows
  // `value`.
  //
  // Clamping on every keystroke instead — which is what this did — makes the
  // field impossible to type into as soon as `min` has more than one digit.
  // On a 1010–1200 ticket book `min` is 1009, so the first "1" of "1150"
  // clamped straight to 1009, the input was rewritten under the caret, and
  // the remaining digits appended to THAT: "10090" → clamped to 1200. Every
  // attempt landed on "none given out" or "whole book given out" and no
  // number in between could be entered at all (R73/R74).
  const [draft, setDraft] = React.useState(null);
  const editing = draft !== null;

  // Half-typed and out-of-range input is never pushed up: the parent keeps
  // the last good value, and the clamp happens once, on the way out. An
  // empty field means "I changed my mind", not "set it to the minimum".
  const commit = () => {
    const digits = (draft || '').replace(/\D/g, '');
    setDraft(null);
    if (digits !== '') onChange(clamp(Number(digits)));
  };

  const step = (delta) => {
    setDraft(null);
    onChange(clamp(Number(value) + delta));
  };

  return (
    <div className="rf-row rf-gap-8">
      <button type="button" className="rf-step-btn" onClick={() => step(-1)}>
        –
      </button>
      <input
        className="rf-input rf-input-big"
        value={editing ? draft : value}
        inputMode="numeric"
        // Select on focus so tapping the field and typing replaces the
        // number rather than appending to it — this is a counter, and the
        // usual move is "the last ticket is now N", not an edit of a digit.
        onFocus={(e) => {
          setDraft(value === null || value === undefined ? '' : String(value));
          e.target.select();
        }}
        onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, '');
          setDraft(digits);
          // Keep the live preview honest while they type — the ticket stub
          // and "N tickets in the draw" above this — but only once the
          // digits actually make a number inside the book.
          const n = Number(digits);
          if (digits !== '' && n >= min && n <= max) onChange(n);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
      />
      <button type="button" className="rf-step-btn" onClick={() => step(1)}>
        +
      </button>
    </div>
  );
}

export function MoneyInput({
  value,
  onChange,
  placeholder = '0',
  big,
  prefix = '$',
}) {
  return (
    <div className={`rf-money ${big ? 'rf-money-big' : ''}`}>
      <span className="rf-money-prefix">{prefix}</span>
      <input
        value={value}
        inputMode="numeric"
        placeholder={placeholder}
        onChange={(e) =>
          onChange(e.target.value.replace(/\D/g, '').slice(0, 7))
        }
      />
    </div>
  );
}

export function Notice({ Icon, children, tone = 'lavender' }) {
  const tones = {
    lavender: { bg: LAVENDER_SOFT, fg: INDIGO, border: 'transparent' },
    amber: { bg: '#fdf3e2', fg: '#b46a00', border: '#f0d9a8' },
    red: { bg: '#fdeaea', fg: '#c62828', border: '#f2c4c4' },
    slate: { bg: BG, fg: SLATE, border: 'transparent' },
  }[tone];
  return (
    <div
      className="rf-notice"
      style={{
        background: tones.bg,
        color: tones.fg,
        borderColor: tones.border,
      }}
    >
      {Icon && <Icon style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }} />}
      <span>{children}</span>
    </div>
  );
}

export function Spinner({ size = 18, color = '#fff' }) {
  return (
    <span
      className="rf-spinner"
      style={{
        width: size,
        height: size,
        borderColor: `${color}55`,
        borderTopColor: color,
      }}
    />
  );
}

/**
 * Keeps a rendering bug in the raffle screen from blanking the whole app.
 * Nothing is lost by reloading: every raffle's state lives on the server.
 */
export class RaffleErrorBoundary extends React.Component {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    console.error('[Raffle] render failed', error, info && info.componentStack);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="rf-root rf-pad">
        <Notice tone="red">
          The raffle screen hit an error. Nothing was lost — an open raffle
          picks up where it left off after a reload.
        </Notice>
        <button
          type="button"
          className="rf-btn rf-btn-navy rf-w100"
          style={{ marginTop: 12 }}
          onClick={() => window.location.reload()}
        >
          Reload
        </button>
      </div>
    );
  }
}
